import { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { FormDialog } from '@/components/dialog/FormDialog';
import { Field } from '@/components/form/Field';
import { SelectField } from '@/components/form/SelectField';
import { useSubmit } from '@/hooks/useSubmit';
import type MemberModel from '@/models/entities/MemberModel';
import { memberUrn } from '@/models/entities/MemberModel';
import TeamMemberModel from '@/models/entities/TeamMemberModel';
import type TeamModel from '@/models/entities/TeamModel';
import { teamUrn } from '@/models/entities/TeamModel';
import { TEAM_ROLE } from '@/models/references/references';
import { resolveReference, useReferenceOptions } from '@/stores/useReferenceOptions';
import { MemberCombobox } from './MemberCombobox';

/**
 * Ajout d'un employé de la société à l'équipe (POST /team_members) : choix de l'employé (déjà dans une équipe : grisé)
 * et de son rôle dans l'équipe, Manager grisé si l'équipe en a déjà un.
 */
export function AddTeamMemberDialog({
  team,
  manager,
  initialRole,
  onClose,
  onSaved,
}: {
  team: TeamModel;
  /** Manager actuel de l'équipe (un seul possible) */
  manager: TeamMemberModel | null;
  initialRole: string;
  onClose: () => void;
  onSaved: (personName: string) => void;
}) {
  const roles = useReferenceOptions('teamRoles');
  const [selected, setSelected] = useState<MemberModel | null>(null);
  const [teamRole, setTeamRole] = useState(
    manager && initialRole === TEAM_ROLE.MANAGER ? TEAM_ROLE.TEAMMATE : initialRole,
  );
  const { submit, submitting, error } = useSubmit();

  const save = () =>
    submit(async () => {
      if (!selected) return;
      await new TeamMemberModel().save(
        {
          team: teamUrn(team.id),
          member: memberUrn(selected.id),
          teamRole: (await resolveReference('teamRoles', teamRole)).urn,
        },
        { silentError: true },
      );
      onSaved(selected.fullName);
    });

  return (
    <FormDialog
      title={`Ajouter à l'équipe ${team.name}`}
      description="Un employé ne fait partie que d'une équipe à la fois."
      submitLabel="Ajouter"
      submitIcon={UserPlus}
      canSubmit={!!selected && roles.length > 0}
      submitting={submitting}
      error={error}
      onSubmit={save}
      onClose={onClose}
    >
      <Field id="team-member" label="Employé" required>
        <MemberCombobox
          id="team-member"
          companyId={team.companyId}
          teamId={Number(team.id)}
          value={selected}
          onChange={setSelected}
        />
      </Field>
      <SelectField
        id="team-role"
        label="Rôle dans l'équipe"
        required
        value={teamRole}
        onChange={(e) => setTeamRole(e.target.value)}
      >
        {roles.map((role) => {
          const taken = role.reference === TEAM_ROLE.MANAGER && !!manager;
          return (
            <option key={role.reference} value={role.reference} disabled={taken}>
              {role.label}
              {taken && ` (déjà : ${manager.fullName})`}
            </option>
          );
        })}
      </SelectField>
    </FormDialog>
  );
}
